import { Table } from "antd";
import assign from "lodash/assign";
import cloneDeep from "lodash/cloneDeep";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { TAB_PARAM_KEYS } from "@/constants/TabKeys";
import { useSectionsFromMenu } from "@/hooks/forms/useSectionsFromMenu";

import SectionNameCell from "./SectionNameCell";
import SectionTags from "./SectionTags";
import { TreeModifier } from "./TreeModifier";

const TableOfContents = ({
  menu,
  childrenKey = "children",
  tabName = TAB_PARAM_KEYS.CONTEXT_TAB_KEY,
  onSectionSelected,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Get the template section
  const layoutIndex = parseInt(searchParams.get("layout_index"));
  const repetitionIndex = parseInt(searchParams.get("repetition_index"));
  const observationId = parseInt(searchParams.get("observation_id"));
  const tab = searchParams.get(tabName);
  const { sections, current, parent } = useSectionsFromMenu(
    cloneDeep(menu),
    repetitionIndex,
    layoutIndex,
    observationId,
    childrenKey
  );
  const rootRowId = parent?.row_index ?? 0;
  const rowId = current?.row_index ?? rootRowId;

  // Set selected table of contents section from url
  // If no row_id query param then select root row
  const [selectedRows, setSelectedRows] = useState([rowId]);

  const sectionQuery = {
    layout_index: null,
    repetition_index: null,
    asset_id: null,
    tab: null,
  };

  // find wanted section by layout index
  if (layoutIndex) {
    // context: intervention template
    const section = TreeModifier.find(sections, "templates", (tree) => {
      return tree.layout_index === layoutIndex;
    });

    if (section) {
      const { layout_index } = section;

      assign(sectionQuery, {
        layout_index,
        [tabName]: tab,
      });
    }
  }

  const columns = [
    {
      dataIndex: "name",
      render: (_name, section) => <SectionNameCell {...section} />,
    },
    {
      dataIndex: "progress",
      render: (_progress, section) => <SectionTags section={section} />,
      align: "right",
      width: "20px",
    },
  ];

  /**
   * Update url query with layout index from child dataset section
   *
   * @param {object} section
   */
  const handleParentLayoutIndex = (section) => {
    if (!layoutIndex && section?.layout_index) {
      // When current page has been loaded from an observation page, use replace
      // instead of push to avoid double click on browser back button.
      const replace = searchParams.get("observation_id") ? true : false;
      handleSectionChange({ section, replace });
    }
  };

  const handleScroll = () => {
    const row = document.querySelector(`tr[data-row-key='${rowId}']`);

    if (row) {
      row.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  const handleSectionChange = ({ section, replace = false }) => {
    if (!section) {
      return;
    }

    const searchParams = new URLSearchParams();
    searchParams.append("layout_index", section.layout_index);

    if (section.repetition_index) {
      searchParams.append("repetition_index", section.repetition_index);
    }
    if (section.asset?.id) {
      searchParams.append("asset_id", section.asset.id);
    }

    if (tab) {
      searchParams.append(tabName, tab);
    }

    onSectionSelected?.(section);
    setSearchParams(searchParams, { replace });
    setSelectedRows([section.row_index]);
  };

  useEffect(() => {
    handleParentLayoutIndex(current);
    if (!current && parent) {
      handleSectionChange({ section: parent });
    }
  }, [menu, current, parent]);

  return sections ? (
    <Table
      dataSource={sections}
      columns={columns}
      pagination={false}
      showHeader={false}
      size="small"
      expandable={{
        defaultExpandAllRows: true,
        childrenColumnName: childrenKey,
      }}
      rowKey="row_index"
      rowSelection={{
        type: "radio",
        selectedRowKeys: selectedRows,
        columnWidth: 0, // hides checkbox selection column
        renderCell: () => "", // render nothing inside
      }}
      onRow={(section) => {
        handleScroll();
        return {
          onClick: () => handleSectionChange({ section }),
        };
      }}
    />
  ) : (
    <></>
  );
};

TableOfContents.propTypes = {
  menu: PropTypes.array,
  childrenKey: PropTypes.string,
  tabName: PropTypes.string,
  onSectionSelected: PropTypes.func,
};

export default TableOfContents;
